// src/pages/Posts/usePosts.js
import { useEffect, useState } from "react";
import { fetchPostsByAccount, createPost, deletePost } from "../../api/posts"; // ✅ changed
import { loadSubscription } from "../../api/users";
import { useParams } from "react-router-dom";

export default function usePosts() {
  const { kind: routeKind } = useParams();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);

  const [subscription, setSubscription] = useState(null);
  const [profileSlug, setProfileSlug] = useState("");

  const [postKindOptions, setPostKindOptions] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [newPostKind, setNewPostKind] = useState("");
  const [createErr, setCreateErr] = useState(null);

  const labelize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  useEffect(() => {
    let on = true;

    (async () => {
      setBusy(true);
      try {
        const sub = await loadSubscription();
        if (!on) return;

        setSubscription(sub);

        // ✅ postKinds come from subscription.plan.postKinds
        const postKinds = sub?.plan?.postKinds || {};
        const opts = Object.entries(postKinds)
          .filter(([, cfg]) => cfg?.enabled !== false)
          .map(([value]) => ({ value, label: labelize(value) }));

        setPostKindOptions(opts);
        setNewPostKind(routeKind || opts[0]?.value || "");

        // ✅ get accountId from subscription
        const accountId =
          sub?.account?.id || sub?.accountId || sub?.account?.accountId;

        if (!accountId) throw new Error("Missing accountId in subscription");

        // ✅ fetch posts by accountId
        const data = await fetchPostsByAccount(accountId);
        if (!on) return;

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.result)
          ? data.result
          : Array.isArray(data?.posts)
          ? data.posts
          : [];

        list.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
        const profile = list.find(post =>
          post.kind === "profile" && post.active === true && String(post.slug || "").trim()
        );
        setProfileSlug(String(profile?.slug || "").replace(/^\/+|\/+$/g, ""));
        setRows(routeKind ? list.filter(post => post.kind === routeKind) : list);
      } catch (e) {
        console.error("Posts load failed:", e);
        if (on) setErr(e.message || "Failed to load posts");
      } finally {
        if (on) setBusy(false);
      }
    })();

    return () => (on = false);
  }, [routeKind]);

  const onAdd = async (payloadFromForm) => {
    const title = (payloadFromForm?.title ?? newTitle).trim();
    const kind = routeKind || payloadFromForm?.kind || newPostKind;

    if (!title) return setCreateErr("Please enter a title.");
    if (!kind) return setCreateErr("No post kinds available.");

    setCreateErr(null);

    try {
      const accountId = subscription?.account?.id;

      if (!accountId) throw new Error("Missing account id in subscription");

      const created = await createPost({
        accountId, // ✅ IMPORTANT
        title,
        kind, // keep if backend uses kind; if backend uses postKind, map it there instead
        // postKind: kind, // optional if your backend expects postKind
      });

      setRows((prev) => [created, ...prev]);
      setNewTitle("");
      setNewPostKind(routeKind || postKindOptions[0]?.value || "");
    } catch (err) {
      console.error("Add post failed:", err);
      setCreateErr(err.message || "Failed to create post");
    }
  };

  const onDeleteLocal = async (id) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    const deletedPost = rows.find(post => String(post.id || post.postId) === String(id));
    setRows(prev => prev.filter(post => String(post.id || post.postId) !== String(id)));
    try {
      await deletePost(id);
      const accountId = subscription?.account?.id;
      if (!accountId) return;
      const refreshed = await fetchPostsByAccount(accountId);
      const list = Array.isArray(refreshed)
        ? refreshed
        : refreshed?.result || refreshed?.posts || [];
      list.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
      setRows(routeKind ? list.filter(post => post.kind === routeKind) : list);
    } catch (error) {
      if (deletedPost) setRows(prev => [deletedPost, ...prev]);
      setErr(error.message || "Failed to delete post");
    }
  };

  const currentKindConfig = subscription?.plan?.postKinds?.[routeKind];
  const currentKindLimit = currentKindConfig?.limit == null
    ? null
    : Number(currentKindConfig.limit);
  const canCreate = !Number.isFinite(currentKindLimit) || rows.length < currentKindLimit;

  return {
    busy,
    err,
    rows,
    profileSlug,
    subscription,
    postKindOptions,
    newTitle,
    newPostKind,
    createErr,
    setNewTitle,
    setNewPostKind,
    onAdd,
    onDeleteLocal,
    canCreate,
    currentKindLimit,
  };
}
